import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

// Append-only event-log timeline for a booking (W2.1,
// docs/plans/event-log-w21-handoff-spec.md §6), served by the new-backend
// admin endpoint `GET /admin/order/:orderId/events`. Producers may ship new
// event types before the UI knows them — consumers must render unknown
// `eventType`s generically (raw label fallback), never crash.
export interface OrderEvent {
    id: string;
    occurredAt: string;
    recordedAt: string;
    /** Dot-namespaced registry name, e.g. 'usage.locker.open_commanded'. */
    eventType: string;
    entityType: string;
    entityId: string;
    actor: {
        /** 'user' | 'admin' | 'system' | 'ai_agent' */
        type: string;
        /** e.g. the acting admin's email; null when not recorded. */
        display: string | null;
    };
    source: string;
    correlationId: string | null;
    payload: Record<string, unknown> | null;
}

export interface OrderEventsResponse {
    orderId: string;
    /** occurred_at ascending; empty when the order has no events. */
    events: OrderEvent[];
}

export const orderEventsApiSlice = createApi({
    reducerPath: 'orderEventsApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['OrderEvents'],
    endpoints: (builder) => ({
        getOrderEvents: builder.query<OrderEventsResponse, { orderId: string }>({
            query: ({ orderId }) => ({
                url: `/order/${orderId}/events`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { orderId }) => [
                { type: 'OrderEvents', id: orderId },
            ],
        }),
    }),
});

// The lazy hook backs `useCustomerMailHint`: the cancel dialog needs this read
// only at the moment someone clicks "Stornieren", and making every
// booking-details view pay for it would be a request nobody asked for.
export const { useGetOrderEventsQuery, useLazyGetOrderEventsQuery } =
    orderEventsApiSlice;
